import { type HttpClientError } from 'core/infra/http/HttpClient'

import {
  type Employee,
  type NewEmployee,
  type Role,
  type SelectableRoles,
} from '../domain/Employee'
import { type PreferredTrackingMethod } from '../domain/PreferredTrackingMethod'
import { type TrackingMethod, type TrackingProvider } from '../domain/Tracking'
import {
  type AddEmployeeResponseError,
  type RawAddEmployeeResponseError,
} from './CarrierRepository'

export type RawEmployee = {
  carrier: string
  email: string
  name: string | null
  phone_number: string
  roles: Role[]
  uuid: string
}

type RawTrackingProviders =
  | 'project-44'
  | 'trucker-tools'
  | 'edi-platform-core'
  | 'loads-app'
  | 'quick-track'
  | 'shipper-guide-user'

export type RawPreferredTrackingMethod = {
  tracking_method: 'manual' | 'eld' | 'edi' | 'mobile' | 'api' | null
  tracking_provider?: RawTrackingProviders | null
}

export type RawTrackingHistory = {
  has_previous_tracking: boolean
}

export type NewRawEmployee = Pick<RawEmployee, 'name' | 'phone_number' | 'roles'>

export function mapApiToEmployee(employee: RawEmployee): Employee {
  return {
    carrier: employee.carrier,
    email: employee.email,
    name: employee.name,
    phoneNumber: employee.phone_number,
    roles: employee.roles,
    uuid: employee.uuid,
  }
}

export function mapHttpClientErrorToAddEmployeeResponseError(
  error: HttpClientError<RawAddEmployeeResponseError>
): AddEmployeeResponseError {
  const rawErrors = error.response?.data

  return {
    phoneNumber: rawErrors?.phone_number[0],
  }
}

export function mapApiToEmployeeList(employees: RawEmployee[]): Employee[] {
  return employees.map(mapApiToEmployee)
}

export function mapRolesToApi(roles: SelectableRoles) {
  return roles.map(role => role.value)
}

export function mapPhoneToApi(phone: string): string {
  return phone.replace(/(-|\s)/g, '')
}

export function mapEmployeeToApi(employee: NewEmployee): NewRawEmployee {
  return {
    name: employee.name,
    phone_number: mapPhoneToApi(employee.phoneNumber),
    roles: mapRolesToApi(employee.roles),
  }
}

export function mapPreferredTrackingMethodToApi(
  method: PreferredTrackingMethod
): RawPreferredTrackingMethod {
  return {
    tracking_method: method.method as RawPreferredTrackingMethod['tracking_method'],
    tracking_provider: (method.provider as RawPreferredTrackingMethod['tracking_provider']) ?? null,
  }
}

export function mapApiToPreferredTrackingMethod(
  rawMethod: RawPreferredTrackingMethod | null
): PreferredTrackingMethod | undefined {
  return rawMethod?.tracking_method
    ? {
        method: rawMethod?.tracking_method as TrackingMethod,
        provider: rawMethod?.tracking_provider as TrackingProvider,
      }
    : undefined
}
